/**
 * SEO utility helpers for GigFalcon frontend.
 * Centralised so every page has a consistent title/description pattern,
 * canonical URL construction and JSON-LD structured data.
 */

const SITE_NAME = "GigFalcon";
const BASE_URL  = "https://www.gigfalcon.com";

// ── Title builders ──────────────────────────────────────────────────────────

/**
 * Build a page <title> string.
 * @param {string} pageTitle   – The unique part of the title.
 * @param {string} [suffix]    – Optional override suffix (default: site name).
 * @returns {string}
 */
export function buildTitle(pageTitle, suffix = SITE_NAME) {
  if (!pageTitle) return suffix;
  return `${pageTitle} | ${suffix}`;
}

// ── Canonical URL builder ───────────────────────────────────────────────────

/**
 * Build a canonical URL from a path, always using the production domain
 * and stripping query params / trailing slashes.
 * @param {string} path  – e.g. "/gig/abc123/detail"
 * @returns {string}
 */
export function buildCanonical(path) {
  const clean = path.split("?")[0].replace(/\/$/, "") || "/";
  return BASE_URL + clean;
}

// ── Description helpers ─────────────────────────────────────────────────────

/**
 * Truncate a string to `maxLen` characters and append "…" if needed.
 * Safe to call with undefined/null.
 * @param {string|undefined|null} text
 * @param {number} [maxLen=155]
 * @returns {string}
 */
export function truncate(text, maxLen = 155) {
  if (!text) return "";
  return text.length <= maxLen ? text : text.slice(0, maxLen - 1) + "…";
}

// ── Structured data (JSON-LD) builders ──────────────────────────────────────

/**
 * Organisation JSON-LD — used on the homepage.
 * @returns {object}
 */
export function buildOrganizationLD() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "GigFalcon",
    url: BASE_URL,
    logo: `${BASE_URL}/logo192.png`,
    sameAs: [
      "https://www.facebook.com/profile.php?id=61556146895847",
      "https://www.instagram.com/gigfalcon01/",
      "https://www.linkedin.com/company/gigfalcon/",
      "https://www.youtube.com/@gigfalcon7422",
      "https://www.tiktok.com/@gigfalcon",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@gigfalcon.com",
      contactType: "customer support",
    },
  };
}

/**
 * WebSite JSON-LD with SearchAction — used on the homepage.
 * @returns {object}
 */
export function buildWebSiteLD() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "GigFalcon",
    url: BASE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${BASE_URL}/?query={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * Service (Gig) JSON-LD — used on GigDetail pages.
 * @param {import('../types/server').GigDetailData} data
 * @param {string} canonicalUrl
 * @returns {object}
 */
export function buildServiceLD(data, canonicalUrl) {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.title,
    description: truncate(data.description, 300),
    url: canonicalUrl,
    provider: {
      "@type": "Person",
      name: data.createdBy?.username || data.createdBy?.name || "GigFalcon Seller",
      url: data.createdBy?._id
        ? `${BASE_URL}/profile/${data.createdBy._id}`
        : undefined,
    },
    serviceType: data.category?.name,
    areaServed: "Worldwide",
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      priceSpecification: {
        "@type": "PriceSpecification",
        minPrice: data.priceFrom,
        maxPrice: data.priceTo,
        priceCurrency: "USD",
      },
      availability: "https://schema.org/InStock",
    },
  };
  if (data.attachments?.[0]?.imgUrl) {
    ld.image = data.attachments[0].imgUrl;
  }
  if (data.rating) {
    ld.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: data.rating,
      ratingCount: data.completedOrdersCount || 1,
      bestRating: 5,
      worstRating: 1,
    };
  }
  return ld;
}

/**
 * JobPosting JSON-LD — used on JobDetail pages.
 * @param {import('../types/server').JobDetailData} data
 * @param {string} canonicalUrl
 * @returns {object}
 */
export function buildJobPostingLD(data, canonicalUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: data.title || truncate(data.description, 60),
    description: truncate(data.description, 500),
    url: canonicalUrl,
    datePosted: data.createdAt
      ? new Date(data.createdAt).toISOString().split("T")[0]
      : undefined,
    validThrough: data.deadline
      ? new Date(data.deadline).toISOString().split("T")[0]
      : undefined,
    hiringOrganization: {
      "@type": "Organization",
      name: data.createdBy?.username || "GigFalcon Employer",
      sameAs: data.createdBy?._id
        ? `${BASE_URL}/profile/${data.createdBy._id}`
        : BASE_URL,
    },
    jobLocation: data.location?.address
      ? {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            streetAddress: data.location.address,
          },
        }
      : {
          "@type": "Place",
          name: "Remote / Various",
        },
    baseSalary: data.budgetMin != null
      ? {
          "@type": "MonetaryAmount",
          currency: "USD",
          value: {
            "@type": "QuantitativeValue",
            minValue: data.budgetMin,
            maxValue: data.budgetMax,
            unitText: "TOTAL",
          },
        }
      : undefined,
    employmentType: data.workType === "remote" ? "TELECOMMUTE" : "CONTRACTOR",
  };
}

/**
 * Person JSON-LD — used on public Profile pages.
 * @param {import('../types/server').UserProfileData} data
 * @param {string} canonicalUrl
 * @returns {object}
 */
export function buildPersonLD(data, canonicalUrl) {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: [data.name, data.lastname].filter(Boolean).join(" ") || data.username,
    url: canonicalUrl,
    jobTitle: data.sellerStats?.category?.name || undefined,
    worksFor: {
      "@type": "Organization",
      name: "GigFalcon",
      url: BASE_URL,
    },
  };
  if (data.profileImg?.imgUrl) ld.image = data.profileImg.imgUrl;
  if (data.location?.city)     ld.address = { "@type": "PostalAddress", addressLocality: data.location.city, addressCountry: data.location.country };
  if (data.rating) {
    ld.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: data.rating,
      ratingCount: data.sellerStats?.completedOrdersCount || 1,
      bestRating: 5,
      worstRating: 1,
    };
  }
  return ld;
}

/**
 * BreadcrumbList JSON-LD.
 * @param {Array<{name: string, url: string}>} items
 * @returns {object}
 */
export function buildBreadcrumbLD(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * ItemList JSON-LD — used on category listing pages.
 * @param {string} name  – List name
 * @param {string} url   – Page URL
 * @param {Array<{name:string,url:string}>} items
 * @returns {object}
 */
export function buildItemListLD(name, url, items) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url,
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      url: item.url,
    })),
  };
}

/**
 * Serialize a JSON-LD object to a script tag string.
 * Use inside <Helmet> as:
 *   <script type="application/ld+json">{jsonLdString(buildOrganizationLD())}</script>
 * @param {object} ld
 * @returns {string}
 */
export function jsonLdString(ld) {
  return JSON.stringify(ld);
}
